import {
  CartEvents,
  type AddToCartPayload,
  type ChangeLinePayload,
  type UpdateCartPayload,
} from './core';
import { CartService } from './cart-service';
import { SimpleCartService } from './simple-cart-service';
import { createLogger } from '@/lib/logger';

/**
 * Listens for `mini-cart:*` command events on `window` and delegates to `CartService`.
 *
 * Commands handled:
 * - `mini-cart:get` → fetch cart
 * - `mini-cart:add` → add items
 * - `mini-cart:change` → change line item quantity/properties
 * - `mini-cart:update` → update note/attributes
 * - `mini-cart:clear` → clear cart
 */
export class CartCommandController {
  private abortController: AbortController | null = null;
  private logger = createLogger('cart-command-controller');

  /**
   * @param service CartService instance that performs the actual operations.
   * @param cartApi Wrapper used for line-addressed changes so external
   *   `mini-cart:change` callers get the same queueing and stale-key recovery
   *   as the mini-cart's own controls.
   */
  constructor(
    private service: CartService,
    private cartApi: SimpleCartService,
  ) {}

  /**
   * Start listening for command events. Idempotent.
   */
  start(): void {
    if (this.abortController) return;
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    this.logger.info('Starting command controller');

    this.logger.info(`Binding ${CartEvents.CmdGet}`);
    window.addEventListener(CartEvents.CmdGet, this.onGet, { signal });

    this.logger.info(`Binding ${CartEvents.CmdAdd}`);
    window.addEventListener(
      CartEvents.CmdAdd,
      (e: Event) => this.onAdd(e as CustomEvent<AddToCartPayload>),
      { signal },
    );

    this.logger.info(`Binding ${CartEvents.CmdChange}`);
    window.addEventListener(
      CartEvents.CmdChange,
      (e: Event) => this.onChange(e as CustomEvent<ChangeLinePayload>),
      { signal },
    );

    this.logger.info(`Binding ${CartEvents.CmdUpdate}`);
    window.addEventListener(
      CartEvents.CmdUpdate,
      (e: Event) => this.onUpdate(e as CustomEvent<UpdateCartPayload>),
      { signal },
    );

    this.logger.info(`Binding ${CartEvents.CmdClear}`);
    window.addEventListener(CartEvents.CmdClear, this.onClear, { signal });
  }

  /**
   * Stop listening for command events and release resources.
   */
  stop(): void {
    this.logger.info('Stopping command controller');
    this.abortController?.abort();
    this.abortController = null;
  }

  /**
   * Handle `mini-cart:get`.
   */
  private onGet = async () => {
    this.logger.info('Getting cart via command controller');
    await this.service.get();
  };

  /**
   * Handle `mini-cart:add`.
   * @param e CustomEvent carrying `AddToCartPayload`.
   */
  private onAdd = async (e: CustomEvent<AddToCartPayload>) => {
    this.logger.info('Adding item via command controller', e.detail);
    const payload = e.detail;
    await this.service.runExclusive(() => this.service.add(payload));
  };

  /**
   * Handle `mini-cart:change`.
   * @param e CustomEvent carrying `ChangeLinePayload`.
   */
  private onChange = async (e: CustomEvent<ChangeLinePayload>) => {
    this.logger.info('Changing item via command controller', e.detail);
    const payload = e.detail;

    // Key-addressed changes go through cartApi, which resolves the key against
    // the live cart inside the mutation queue. Line-addressed changes have no
    // key to recover, so they only need serialising.
    if (payload.id) {
      await this.cartApi.setQuantityByKey(payload.id, payload.quantity, payload.properties);
      return;
    }

    await this.service.runExclusive(() => this.service.change(payload));
  };

  /**
   * Handle `mini-cart:update`.
   * @param e CustomEvent carrying `UpdateCartPayload`.
   */
  private onUpdate = async (e: CustomEvent<UpdateCartPayload>) => {
    this.logger.info('Updating cart via command controller', e.detail);
    const payload = e.detail;
    await this.service.runExclusive(() => this.service.update(payload));
  };

  /**
   * Handle `mini-cart:clear`.
   */
  private onClear = async () => {
    this.logger.info('Clearing cart via command controller');
    await this.service.runExclusive(() => this.service.clear());
  };
}
